/** @jsxImportSource @emotion/react */
import { useRef, useState } from 'react';
import { Button, Checkbox, FormControlLabel, TextField } from '@mui/material';
import styled from '@emotion/styled';
import { closeSession, fetchSession } from 'apis/chatbot/Chatbot';
import {
  CALL_DEFS,
  CallDef,
  CallGroup,
  getSampleBody,
  GROUP_HINTS,
  GROUP_TITLES,
  GROUPS,
} from 'pages/sample/AiRefSamples';
import useSessionStore from 'stores/useSessionStore';
import { API_BASE_URL } from 'utils/ApiUtil';

/**
 * AI Server 통합 인터페이스 테스트 패널 (BE AiRefService 경유)
 *
 * chat(일반챗/범위챗/챗중단) · info(마스터/데이터/공장) · inference(리포트 생성)를
 * 화면에서 직접 호출해보기 위한 개발용 도구다. 인터페이스마다 요청 바디 편집기와 응답
 * 박스를 나란히 두어, 무엇을 보냈고 무엇이 돌아왔는지 한 자리에서 보이게 했다.
 *
 * ⚠ axios(callApi)가 아니라 fetch로 직접 호출한다. 이유가 둘 있다.
 *   1) chat은 BE가 SseEmitter로 흘려보내서, axios로 받으면 스트림이 닫힐 때까지
 *      resolve되지 않아 화면이 멈춘 것처럼 보인다.
 *   2) info·inference는 CommonResponse 봉투가 아니라 HttpServletResponse에 직접 쓰는
 *      구조라, ApiUtil 인터셉터를 태우면 HTTP 상태코드가 버려지고 빈 본문만 남아
 *      **성공인지 실패인지조차 알 수 없다.** 여기서는 status·헤더·본문을 날것으로 찍는다.
 *
 * 세션 헤더(x-language 등)는 ApiUtil 인터셉터가 붙이던 것과 동일하게 맞춰 보낸다.
 *
 * 호출 목록과 요청 바디 샘플은 이 파일에 두지 않는다 → pages/sample/AiRefSamples.ts
 */

/** 세션유형코드 — 챗봇과 동일하게 발급받는다 */
const SESSION_TP_CHATBOT = 'CHATBOT';

/** AI 응답이 약 180초라 추론 호출에만 넉넉히 건다 */
const AI_TIMEOUT_MS = 240000;

const LOG_SEPARATOR = '\n──────────────────────────────\n';

const HINT_ROOT =
  'BE(AiRefService)를 경유해 AI 서버로 나가는 인터페이스를 호출합니다. ' +
  'URL과 요청 바디는 그대로 고쳐서 보낼 수 있고, 응답은 오른쪽 박스와 ⑤ 로그에 남습니다.';

const PLACEHOLDER_SESSION = '발급 버튼을 누르거나 기존 세션 ID를 붙여넣으세요';
const EMPTY_ANSWER = '아직 호출하지 않았습니다.';
const WAITING_ANSWER = '응답 대기 중…';
const NO_CHUNK_ANSWER = '(data: 청크 없음 — ⑤ 로그의 RAW에서 원본을 확인하세요)';
const NO_BODY_ANSWER = '(응답 본문 없음 — 위 상태코드로 판단하세요)';
const NO_REQUEST_BODY = '(요청 바디 없이 호출합니다)';
const EMPTY_LOG = '호출 기록이 없습니다.';
const MSG_NO_SESSION = '세션 ID가 없습니다. 먼저 세션을 발급하세요.';

interface LogEntry {
  id: number;
  at: string;
  label: string;
  /** REQ 요청 / RES 응답 / ERR 오류 / RAW 가공 전 수신 본문 */
  kind: 'REQ' | 'RES' | 'ERR' | 'RAW';
  text: string;
}

const pretty = (v: unknown): string => {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};

/** JSON이면 보기 좋게, 아니면 받은 문자열 그대로 */
const formatMaybeJson = (text: string): string => {
  try {
    return JSON.stringify(JSON.parse(text), null, 2);
  } catch {
    return text;
  }
};

/** SseEmitter가 문자열을 JSON으로 감싸 보내는 경우("\"텍스트\"")를 벗겨낸다 */
const unwrapSseData = (data: string): string => {
  if (data.startsWith('"') && data.endsWith('"')) {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  return data;
};

/** ApiUtil 인터셉터가 붙이던 세션 헤더 — fetch로 직접 부르므로 여기서 맞춰준다 */
const sessionHeaders = (): Record<string, string> => {
  const s = useSessionStore.getState();
  return {
    'x-language': String(s.langCd ?? ''),
    'x-timeZone': String(s.gptTmzCd ?? ''),
    'x-timeFormat': String(s.tmzFrmtCd ?? ''),
    'x-prdnProc': String(s.prdnProcCd ?? ''),
    'x-elmCop': String(s.elmCopCd ?? ''),
    'x-mpClsfType': String(s.mpClsfType ?? ''),
    'x-elmPrjId': String(s.elmPrjId ?? ''),
    'accept-language': String(s.langCd ?? ''),
  };
};

/**
 * 이 화면 자체가 스크롤을 갖는다.
 *
 * 앱 셸(App.tsx st.root, MainLayout ContentLayout)이 overflow:hidden이라, 페이지가
 * 스스로 스크롤 영역을 만들지 않으면 아래쪽 카드가 잘려 버튼조차 누를 수 없다.
 * 부모 높이가 확정이 아닐 때도 동작하도록 %가 아니라 vh 기준으로 잡는다.
 */
const Root = styled.div`
  max-height: calc(100vh - 160px);
  overflow-y: auto;
  padding: 0 4px 24px 0;
`;

const Section = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 12px;
  background: #fff;
`;

const SectionTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 8px;
`;

const CallCard = styled.div`
  border-top: 1px solid #ececec;
  padding: 12px 0;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;

const Pane = styled.div`
  flex: 1 1 380px;
  min-width: 320px;
`;

const PaneTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 30px;
  font-size: 12px;
  font-weight: bold;
  color: #424242;
  margin: 8px 0 4px;
`;

const PaneTools = styled.div`
  display: flex;
  gap: 4px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: #757575;
  margin: 4px 0 8px;
  line-height: 1.6;
`;

const LogBox = styled.pre`
  margin: 0;
  padding: 12px;
  height: 320px;
  overflow: auto;
  background: #1f1f1f;
  color: #e0e0e0;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-all;
`;

const AnswerBox = styled.pre`
  margin: 0;
  padding: 12px;
  min-height: 160px;
  max-height: 320px;
  overflow: auto;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #fafafa;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-all;
`;

const AiRefCallTestPanel = () => {
  const [sessionId, setSessionId] = useState<string>('');
  const [autoSession, setAutoSession] = useState<boolean>(true);
  const [calls, setCalls] = useState<CallDef[]>(CALL_DEFS);
  const [responses, setResponses] = useState<Record<string, string>>({});
  /** 호출 진행 중인 카드 — 카드마다 따로 잡는다(한 값으로 잡으면 동시 호출 때 섞인다) */
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  /** 응답 박스 높이 제한을 푼 카드 — 긴 리포트 응답을 통째로 보려고 둔다 */
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const logSeq = useRef<number>(0);
  const abortRef = useRef<AbortController | null>(null);
  /** 진행 중인 요청 — 응답이 안 오면 [중단]으로 끊을 수 있어야 버튼이 잠기지 않는다 */
  const controllersRef = useRef<Record<string, AbortController>>({});

  const addLog = (label: string, kind: LogEntry['kind'], text: string) => {
    logSeq.current += 1;
    const entry: LogEntry = {
      id: logSeq.current,
      at: new Date().toLocaleTimeString('ko-KR', { hour12: false }),
      label,
      kind,
      text,
    };
    setLogs((prev) => [entry, ...prev]);
  };

  const patchCall = (key: string, patch: Partial<CallDef>) =>
    setCalls((prev) => prev.map((c) => (c.key === key ? { ...c, ...patch } : c)));

  const setResponse = (key: string, text: string) =>
    setResponses((prev) => ({ ...prev, [key]: text }));

  const appendResponse = (key: string, text: string) =>
    setResponses((prev) => ({ ...prev, [key]: (prev[key] ?? '') + text }));

  /** 편집기 JSON 파싱 — 실패하면 로그에 남기고 null을 돌려 호출을 막는다 */
  const parseBody = (label: string, raw: string): Record<string, unknown> | null => {
    try {
      const parsed = JSON.parse(raw || '{}');
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        addLog(label, 'ERR', '요청 바디는 JSON 객체여야 합니다.');
        return null;
      }
      return parsed as Record<string, unknown>;
    } catch (e) {
      addLog(label, 'ERR', `요청 바디 JSON 파싱 실패 — ${String(e)}`);
      return null;
    }
  };

  /**
   * 일반 POST — HTTP 상태코드 · content-type · 본문을 가공 없이 남긴다.
   * BE가 본문 없이 200만 주는 엔드포인트가 있어 상태코드를 봐야 성공/실패가 갈린다.
   */
  const postRaw = async (call: CallDef, body: object) => {
    const label = call.title;
    const timeoutMs = call.slow ? AI_TIMEOUT_MS : undefined;
    const controller = new AbortController();
    controllersRef.current[call.key] = controller;
    const timer = timeoutMs ? window.setTimeout(() => controller.abort(), timeoutMs) : undefined;
    try {
      const response = await fetch(`${API_BASE_URL}${call.url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...sessionHeaders() },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      const text = await response.text();
      const shown = text ? formatMaybeJson(text) : NO_BODY_ANSWER;
      // 응답 박스는 상태코드만, 로그에는 content-type까지 남긴다.
      // 본문 길이를 같이 찍어야 "다 안 찍힌 것 같다"를 눈으로 확인할 수 있다.
      const head = `HTTP ${response.status} ${response.statusText} · 본문 ${text.length}자`;
      const contentType = response.headers.get('content-type') ?? '-';
      addLog(label, response.ok ? 'RES' : 'ERR', `${head} (${contentType})\n${shown}`);
      setResponse(call.key, `${head}\n\n${shown}`);
    } catch (e) {
      const aborted = (e as Error).name === 'AbortError';
      const msg = aborted ? `중단 또는 타임아웃(${timeoutMs}ms)` : pretty(e);
      addLog(label, 'ERR', msg);
      setResponse(call.key, msg);
    } finally {
      if (timer) window.clearTimeout(timer);
      delete controllersRef.current[call.key];
    }
  };

  /** SSE POST — 도착하는 대로 RAW 로그에 남기고 data: 청크만 응답 박스에 이어붙인다 */
  const postStream = async (call: CallDef, body: object) => {
    const label = call.title;
    const controller = new AbortController();
    abortRef.current = controller;
    controllersRef.current[call.key] = controller;
    setResponse(call.key, '');
    try {
      const response = await fetch(`${API_BASE_URL}${call.url}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          ...sessionHeaders(),
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      const head = `HTTP ${response.status} ${response.statusText}`;
      const contentType = response.headers.get('content-type') ?? '-';
      addLog(label, response.ok ? 'RES' : 'ERR', `${head} (${contentType})`);
      setResponse(call.key, `${head}\n\n`);
      if (!response.ok || !response.body) return;

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';
      let chunks = 0;
      let received = 0;
      let done = false;
      while (!done) {
        const read = await reader.read();
        done = read.done;
        if (done) break;

        const raw = decoder.decode(read.value, { stream: true });
        addLog(label, 'RAW', raw);
        buffer += raw;

        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        lines.forEach((line) => {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) return;
          const data = unwrapSseData(trimmed.slice(5).trim());
          if (!data) return;
          chunks += 1;
          received += data.length;
          appendResponse(call.key, data);
        });
      }
      addLog(label, 'RES', `수신 완료 — data: 청크 ${chunks}건, 본문 ${received}자`);
      if (chunks === 0) appendResponse(call.key, NO_CHUNK_ANSWER);
    } catch (e) {
      const aborted = (e as Error).name === 'AbortError';
      const msg = aborted ? '사용자 중단' : pretty(e);
      addLog(label, aborted ? 'RES' : 'ERR', msg);
      appendResponse(call.key, `\n${msg}`);
    } finally {
      delete controllersRef.current[call.key];
    }
  };

  /**
   * 전송 — chat 그룹만 sessionId를 채워 보낸다(info·inference는 세션을 쓰지 않는다).
   * chatType=cancel은 진행 중인 스트림을 먼저 끊고 요청을 보낸다.
   */
  const send = async (call: CallDef) => {
    // 바디가 필요 없는 인터페이스는 편집기를 숨겨두므로 빈 객체로 보낸다
    const body = call.noBody ? {} : parseBody(call.title, call.body);
    if (!body) return;

    if (call.group === 'chat' && autoSession) {
      if (!sessionId) {
        addLog(call.title, 'ERR', MSG_NO_SESSION);
        return;
      }
      (body as Record<string, unknown>).sessionId = sessionId;
    }
    if ((body as Record<string, unknown>).chatType === 'cancel') abortRef.current?.abort();

    addLog(call.title, 'REQ', `POST ${call.url}\n${pretty(body)}`);
    setBusy((prev) => ({ ...prev, [call.key]: true }));
    setResponse(call.key, WAITING_ANSWER);
    try {
      if (call.stream) {
        await postStream(call, body);
      } else {
        await postRaw(call, body);
      }
    } finally {
      // 어떤 이유로든 버튼이 잠긴 채 남지 않게 한다
      setBusy((prev) => ({ ...prev, [call.key]: false }));
    }
  };

  /** 진행 중인 호출 끊기 — 응답이 안 와도 버튼이 영영 잠기지 않도록 */
  const abortCall = (key: string) => controllersRef.current[key]?.abort();

  const handleIssueSession = async () => {
    const label = '세션 발급';
    addLog(label, 'REQ', `POST /v1/cmn-session\n${pretty({ sessionTpCd: SESSION_TP_CHATBOT })}`);
    try {
      const id = await fetchSession(SESSION_TP_CHATBOT);
      setSessionId(id);
      addLog(label, 'RES', `sessionId=${id}`);
    } catch (e) {
      addLog(label, 'ERR', pretty(e));
    }
  };

  const handleCloseSession = async () => {
    const label = '세션 종료';
    if (!sessionId) {
      addLog(label, 'ERR', MSG_NO_SESSION);
      return;
    }
    addLog(label, 'REQ', `POST /v1/cmn-session/close\n${pretty({ sessionId })}`);
    try {
      await closeSession(sessionId);
      addLog(label, 'RES', '세션 종료 완료');
      setSessionId('');
    } catch (e) {
      addLog(label, 'ERR', pretty(e));
    }
  };

  const callsOf = (group: CallGroup) => calls.filter((call) => call.group === group);

  const toggleExpanded = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const copyResponse = (key: string) => {
    const text = responses[key] ?? '';
    navigator.clipboard.writeText(text).catch((e) => addLog('응답 복사', 'ERR', pretty(e)));
  };

  const logView =
    logs.length === 0
      ? EMPTY_LOG
      : logs.map((l) => `[${l.at}] [${l.kind}] ${l.label}\n${l.text}`).join(LOG_SEPARATOR);

  return (
    <Root>
      <SectionTitle style={{ fontSize: 16 }}>AI Server 통합 인터페이스 테스트</SectionTitle>
      <Hint>{HINT_ROOT}</Hint>

      <Section>
        <SectionTitle>① 세션 (chat 전용)</SectionTitle>
        <Row>
          <TextField
            label="세션 ID"
            size="small"
            style={{ width: 420 }}
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
            placeholder={PLACEHOLDER_SESSION}
          />
          <Button variant="contained" onClick={handleIssueSession}>
            세션 발급
          </Button>
          <Button variant="outlined" onClick={handleCloseSession}>
            세션 종료
          </Button>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={autoSession}
                onChange={(e) => setAutoSession(e.target.checked)}
              />
            }
            label="chat 요청의 sessionId 자동 주입"
          />
        </Row>
      </Section>

      {GROUPS.map((group) => (
        <Section key={group}>
          <SectionTitle>{GROUP_TITLES[group]}</SectionTitle>
          <Hint>{GROUP_HINTS[group]}</Hint>
          {callsOf(group).map((call) => (
            <CallCard key={call.key}>
              <Row>
                <TextField
                  label={call.title}
                  size="small"
                  style={{ width: 420 }}
                  value={call.url}
                  onChange={(e) => patchCall(call.key, { url: e.target.value })}
                />
                {busy[call.key] ? (
                  <Button variant="outlined" color="error" onClick={() => abortCall(call.key)}>
                    중단
                  </Button>
                ) : (
                  <Button variant="contained" onClick={() => send(call)}>
                    전송
                  </Button>
                )}
                {call.noBody ? null : (
                  <Button
                    variant="text"
                    onClick={() => patchCall(call.key, { body: getSampleBody(call.key) })}
                  >
                    샘플 복원
                  </Button>
                )}
              </Row>
              <Row>
                <Pane>
                  <PaneTitle>요청 바디</PaneTitle>
                  {call.noBody ? (
                    <AnswerBox>{NO_REQUEST_BODY}</AnswerBox>
                  ) : (
                    <TextField
                      size="small"
                      multiline
                      minRows={8}
                      maxRows={20}
                      fullWidth
                      value={call.body}
                      onChange={(e) => patchCall(call.key, { body: e.target.value })}
                    />
                  )}
                </Pane>
                <Pane>
                  <PaneTitle>
                    응답
                    <PaneTools>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => toggleExpanded(call.key)}
                        disabled={!responses[call.key]}
                      >
                        {expanded[call.key] ? '접기' : '전체 펼치기'}
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => copyResponse(call.key)}
                        disabled={!responses[call.key]}
                      >
                        복사
                      </Button>
                    </PaneTools>
                  </PaneTitle>
                  <AnswerBox style={expanded[call.key] ? { maxHeight: 'none' } : undefined}>
                    {responses[call.key] || EMPTY_ANSWER}
                  </AnswerBox>
                </Pane>
              </Row>
            </CallCard>
          ))}
        </Section>
      ))}

      <Section>
        <Row style={{ justifyContent: 'space-between' }}>
          <SectionTitle>⑤ 호출 로그 (최신순)</SectionTitle>
          <Button variant="outlined" size="small" onClick={() => setLogs([])}>
            로그 지우기
          </Button>
        </Row>
        <LogBox>{logView}</LogBox>
      </Section>
    </Root>
  );
};

export default AiRefCallTestPanel;
